import { capture, registerPwaSuperProperties } from './analytics.js';
import { getInstallContext } from './pwaDetection.js';
import { getDebugSnapshot, recordVisit, markInstalled } from './installPromptManager.js';

function platformFromContext(ctx) {
  if (ctx.ios) return 'ios';
  if (ctx.android) return 'android';
  return 'other';
}

export function trackPwaSession({ searchParams = '' } = {}) {
  recordVisit();
  const ctx = getInstallContext();
  // iOS Safari does NOT fire `appinstalled` for Add-to-Home-Screen, so the
  // `installed` flag would stay false forever. Running in standalone mode
  // is itself authoritative evidence of install — backfill the flag so
  // funnel analyses gated on pwa_installed=true count iOS users too.
  // Snapshot BEFORE markInstalled so we can detect the not-installed →
  // installed transition and emit a deduped `pwa_installed` event. iOS
  // Safari never fires `appinstalled`, so without this backfill the
  // install funnel undercounts every iOS user.
  const preSnapshot = getDebugSnapshot();
  if (ctx.standalone) {
    try { markInstalled(); } catch { /* ignore */ }
  }
  const snapshot = getDebugSnapshot();
  // Register super-properties BEFORE the inferred `pwa_installed` capture
  // so the event itself carries pwa_installed=true. The earlier register
  // in analytics.js `loaded` callback snapshotted the pre-markInstalled
  // value (false); without re-registering first, funnels filtered on
  // pwa_installed=true would exclude this very install event. Mirrors
  // the ordering in installPromptCapture.js's accepted/appinstalled paths.
  registerPwaSuperProperties({
    pwa_standalone: ctx.standalone,
    pwa_installed: snapshot.installed,
  });
  if (ctx.standalone && !preSnapshot.installed) {
    capture('pwa_installed', { source: 'standalone_detected', inferred: true });
  }
  const platform = platformFromContext(ctx);

  if (ctx.standalone) {
    let source = null;
    try {
      const params = new URLSearchParams(searchParams || '');
      source = params.get('source');
    } catch { /* ignore */ }
    capture('pwa_launched_standalone', { source });
  }

  capture('pwa_session_start', {
    standalone: ctx.standalone,
    platform,
    visitCount: snapshot.visitCount,
    installed: snapshot.installed,
    daysSinceFirstVisit: snapshot.daysSinceFirstVisit,
  });

  if (ctx.standalone && snapshot.visitCount > 1) {
    capture('pwa_returning_pwa_user', { visitCount: snapshot.visitCount });
  }
}

export const _internal = { platformFromContext };
